import {
  CRMChannel,
  WhatsappMessageModel,
  instagramMessages$,
  twilioMessages$,
  webChatBotMessages$,
} from "@jugl-web/rest-api";
import { InstagramConversationModel } from "@jugl-web/rest-api/integrations/models/InstagramConversation";
import { InstagramMessageModel } from "@jugl-web/rest-api/integrations/models/InstagramMessage/InstagramMessageModel";
import { TwilioConversationModel } from "@jugl-web/rest-api/integrations/models/TwilioConversation";
import {
  TwilioMessageDto,
  TwilioMessageModel,
  transformTwilioMessageDtoToModel,
} from "@jugl-web/rest-api/integrations/models/TwilioMessage";
import { WebChatBotConversationModel } from "@jugl-web/rest-api/integrations/models/WebChatBotConversation";
import { WebChatBotMessageModel } from "@jugl-web/rest-api/integrations/models/WebChatBotMessage";
import { WebChatBotMessageDto } from "@jugl-web/rest-api/integrations/models/WebChatBotMessage/WebChatBotMessageDto";
import { transformWebChatBotMessageDtoToModel } from "@jugl-web/rest-api/integrations/models/WebChatBotMessage/utils";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import { useToast } from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { CRM_INBOX_PREFERENCES_KEY } from "@jugl-web/utils/storage";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { PeopleInfoSidebar } from "@web-src/modules/people/components/PeopleInfoSidebar";
import usePhoenixSocket from "@web-src/modules/socket/providers/usePhoenixSocket";
import { Channel } from "phoenix";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { Subject } from "rxjs";
import {
  ContactInfoDrawer,
  ContactInfoDrawerVariant,
} from "@web-src/modules/contacts/ContactsPage/components/ContactInfoDrawer";
import { useInstagramAccount } from "../InstagramAccountProvider/useInstagramAccount";
import { useWebChatBotAccount } from "../WebChatBotAccountProvider/useWebChatBotAccount";
import { useWhatsappAccount } from "../WhatsappAccountProvider/useWhatsappAccount";
import {
  ChannelListItem,
  ClientSupportContext,
  ClientSupportPageContextValue,
} from "./ClientSupportPageContext";
import { useManageAccountsDrawer } from "../../components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";

type ReplyMessagesMap = Record<
  string,
  | WhatsappMessageModel
  | InstagramMessageModel
  | WebChatBotMessageModel
  | TwilioMessageModel
  | undefined
>;

export const ClientSupportPageProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { entityId } = useEntityProvider();

  const [selectedConversation, setSelectedConversation] = useState<
    | WhatsappConversationModel
    | InstagramConversationModel
    | WebChatBotConversationModel
    | TwilioConversationModel
  >();
  const [selectedChannel, setSelectedChannel] = useState<CRMChannel>(
    CRMChannel.whatsapp
  );
  const [selectedChannelLocal, setSelectedChannelLocal] = useLocalStorage<
    Record<
      string,
      {
        channel: CRMChannel;
      }
    >
  >(CRM_INBOX_PREFERENCES_KEY, {});

  const [replyMessages, setReplyMessages] = useState<ReplyMessagesMap>({});
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [openedUserProfile, setOpenedUserProfile] = useState<string>();
  const [openedContactInfo, setOpenedContactInfo] = useState<{
    conversationId: string;
    variant: ContactInfoDrawerVariant;
  }>();
  const { socket } = usePhoenixSocket();
  const $incomingWhatsappMessages = useRef<
    ClientSupportPageContextValue["incomingWhatsappMessages$"]
  >(new Subject());
  const $incomingInstagramMessages = useRef<
    ClientSupportPageContextValue["incomingInstagramMessages$"]
  >(new Subject());
  const $incomingWebMessages = useRef<
    ClientSupportPageContextValue["incomingWebMessages$"]
  >(new Subject());
  const $incomingTwilioMessages = useRef<
    ClientSupportPageContextValue["incomingTwilioMessages$"]
  >(new Subject());
  const chatbotChannelRef = useRef<Channel | null>(null);
  const { toast } = useToast();
  const { navigateToPage } = useNavigation();
  const { crmConfig, isCRMConfigLoading } = useManageAccountsDrawer();
  const { whatsappAgent } = useWhatsappAccount();
  const { instagramAccount } = useInstagramAccount();
  const { webChatBotAccount } = useWebChatBotAccount();
  const { channel: urlChannel, activeConversation } = useParams<string>();

  const selectConversation = useCallback(
    (
      conversation?:
        | WhatsappConversationModel
        | InstagramConversationModel
        | WebChatBotConversationModel
        | TwilioConversationModel,
      auto?: boolean
    ) => {
      if (isSendingMessage && !auto) {
        toast(
          "Please wait until the message is sent before switching conversations",
          {
            variant: "warning",
          }
        );
        return;
      }
      setSelectedConversation(conversation);

      if (conversation) {
        navigateToPage(
          "clientSupportActiveConversation",
          {
            channel: selectedChannel,
            activeConversation: conversation.conv_id,
          },
          { replacePathOnly: true }
        );
      } else {
        navigateToPage(
          "clientSupportChannel",
          { channel: selectedChannel },
          { replacePathOnly: true }
        );
      }
    },
    [isSendingMessage, toast, selectedChannel, navigateToPage]
  );

  const selectChannel = useCallback(
    (channel: CRMChannel) => {
      if (selectedChannel === channel) return;

      if (isSendingMessage) {
        toast(
          "Please wait until the message is sent before switching conversations",
          {
            variant: "warning",
          }
        );
        return;
      }

      setSelectedChannel(channel);
      setSelectedConversation(undefined);
      setSelectedChannelLocal((prev) => {
        const updated = { ...prev };
        if (entityId) {
          updated[entityId] = {
            channel,
          };
        }
        return updated;
      });
      navigateToPage(
        "clientSupportChannel",
        { channel },
        { replacePathOnly: true }
      );
    },
    [
      entityId,
      isSendingMessage,
      navigateToPage,
      selectedChannel,
      setSelectedChannelLocal,
      toast,
    ]
  );

  const setReplyMessage = useCallback(
    (
      conversationId: string,
      message?:
        | WhatsappMessageModel
        | InstagramMessageModel
        | WebChatBotMessageModel
        | TwilioMessageModel
    ) => {
      setReplyMessages((prev) => ({
        ...prev,
        [conversationId]: message,
      }));
    },
    []
  );

  const refetchTrigger$ = useMemo(() => new Subject<void>(), []);

  const availableChannels: ChannelListItem[] = useMemo(() => {
    if (isCRMConfigLoading) return [];

    const channels: ChannelListItem[] = [];

    // Add Twilio channel
    if (crmConfig?.twilio?.id) {
      channels.push({
        id: CRMChannel.twilio,
      });
    }

    if (webChatBotAccount) {
      channels.unshift({
        id: CRMChannel.chatbot,
        name: webChatBotAccount.name,
      });
    }

    if (instagramAccount) {
      channels.unshift({
        id: CRMChannel.instagram,
        name: instagramAccount.name,
      });
    }

    if (whatsappAgent) {
      channels.unshift({
        id: CRMChannel.whatsapp,
        name: whatsappAgent.name,
      });
    }

    return channels;
  }, [
    isCRMConfigLoading,
    crmConfig?.twilio?.id,
    webChatBotAccount,
    instagramAccount,
    whatsappAgent,
  ]);

  useEffectOnce(() => {
    const isCRMChannel = (value: string): value is CRMChannel => {
      return Object.values(CRMChannel).includes(value as CRMChannel);
    };

    if (urlChannel && isCRMChannel(urlChannel)) {
      setSelectedChannel(urlChannel);
    }
  });

  useEffect(() => {
    if (
      !!availableChannels.length &&
      selectedChannel &&
      !availableChannels.some((channel) => channel.id === selectedChannel)
    ) {
      const newChannel = availableChannels[0].id;
      setSelectedChannel(newChannel);
      navigateToPage(
        "clientSupportChannel",
        { channel: newChannel },
        { replacePathOnly: true }
      );
    }
  }, [availableChannels, navigateToPage, selectedChannel]);

  useEffect(() => {
    const savedChannel = selectedChannelLocal[entityId || ""]?.channel;
    if (savedChannel && availableChannels.length && !activeConversation) {
      const isChannelAvailable = availableChannels.some(
        (channel) => channel.id === savedChannel
      );
      if (isChannelAvailable) {
        setSelectedChannel(savedChannel);
      }
    }
  }, [
    activeConversation,
    availableChannels,
    entityId,
    selectedChannelLocal,
    setSelectedChannel,
  ]);

  const sendWebMessage = useCallback(
    (params: { conv_id: string; conv_name: string; text: string }) => {
      const timestamp = Date.now();

      // Send via socket
      chatbotChannelRef.current?.push("send_chatbot_message", {
        conv_id: params.conv_id,
        conv_name: params.conv_name,
        payload: {
          text: params.text,
          type: "text",
          timestamp,
        },
      });
    },
    []
  );

  useEffect(() => {
    if (!socket) return () => {};

    const whatsappChannel = socket.channel("whatsapp:messages");
    const instagramChannel = socket.channel("instagram:messages");
    const chatbotChannel = socket.channel("chatbot:lobby");
    const twilioChannel = socket.channel("twilio:lobby");

    // Store chatbot channel in ref for sendWebMessage
    chatbotChannelRef.current = chatbotChannel;

    whatsappChannel
      .join()
      .receive("ok", () => {
        // TODO: handle ok
      })
      .receive("error", () => {
        // TODO: handle error
      });

    instagramChannel
      .join()
      .receive("ok", () => {
        // TODO: handle okn
      })
      .receive("error", () => {
        // TODO: handle error
      });

    chatbotChannel
      .join()
      .receive("ok", () => {
        // TODO: handle ok
      })
      .receive("error", () => {
        // TODO: handle error
      });

    twilioChannel
      .join()
      .receive("ok", () => {
        // TODO: handle ok
      })
      .receive("error", () => {
        // TODO: handle error
      });

    whatsappChannel.on(
      "wa_message",
      ({ message }: { message: WhatsappMessageModel }) => {
        $incomingWhatsappMessages.current.next(message);
      }
    );

    instagramChannel.on(
      "ig_message",
      ({ message }: { message: InstagramMessageModel }) => {
        $incomingInstagramMessages.current.next(message);
        instagramMessages$.next({ type: "append", message });
      }
    );

    chatbotChannel.on(
      "chatbot_message",
      ({ message }: { message: WebChatBotMessageDto }) => {
        const transformedMessage =
          transformWebChatBotMessageDtoToModel(message);

        $incomingWebMessages.current.next(transformedMessage);

        webChatBotMessages$.next({
          type: "append",
          message: transformedMessage,
        });
      }
    );

    twilioChannel.on(
      "twilio_message",
      ({ message }: { message: TwilioMessageDto }) => {
        const transformedMessage = transformTwilioMessageDtoToModel(message);
        $incomingTwilioMessages.current.next(transformedMessage);
        twilioMessages$.next({ type: "append", message: transformedMessage });
      }
    );

    return () => {
      whatsappChannel.leave();
      instagramChannel.leave();
      chatbotChannel.leave();
      twilioChannel.leave();
      chatbotChannelRef.current = null;
    };
  }, [socket, refetchTrigger$]);

  const value: ClientSupportPageContextValue = useMemo(
    () => ({
      selectedConversation,
      selectConversation,
      refetchTrigger$,
      replyMessage: selectedConversation
        ? replyMessages[selectedConversation.conv_id]
        : undefined,
      setReplyMessage,
      incomingWhatsappMessages$: $incomingWhatsappMessages.current,
      incomingInstagramMessages$: $incomingInstagramMessages.current,
      incomingWebMessages$: $incomingWebMessages.current,
      incomingTwilioMessages$: $incomingTwilioMessages.current,
      sendWebMessage,
      isSendingMessage,
      setIsSendingMessage,
      openedUserProfile,
      setOpenedUserProfile,
      selectChannel,
      selectedChannel,
      availableChannels,
      setOpenedContactInfo,
    }),
    [
      selectedConversation,
      selectConversation,
      refetchTrigger$,
      replyMessages,
      setReplyMessage,
      sendWebMessage,
      isSendingMessage,
      openedUserProfile,
      selectChannel,
      selectedChannel,
      availableChannels,
    ]
  );

  const contactInfoDrawerData = useMemo(() => {
    if (
      !openedContactInfo ||
      selectedConversation?.conv_id !== openedContactInfo.conversationId
    ) {
      return undefined;
    }

    return {
      contact: selectedConversation.contact_info,
      conversationName: selectedConversation.name,
    };
  }, [openedContactInfo, selectedConversation]);

  return (
    <ClientSupportContext.Provider value={value}>
      <PeopleInfoSidebar
        userId={openedUserProfile}
        isOpen={!!openedUserProfile}
        onRequestClose={() => setOpenedUserProfile(undefined)}
      />
      <ContactInfoDrawer
        conversationId={openedContactInfo?.conversationId}
        conversationName={contactInfoDrawerData?.conversationName}
        contact={contactInfoDrawerData?.contact}
        variant={openedContactInfo?.variant}
        isOpen={!!openedContactInfo}
        onClose={() => setOpenedContactInfo(undefined)}
      />
      {children}
    </ClientSupportContext.Provider>
  );
};
